import type { VersionHistory, VersionHistoryEntry } from '../types/versionHistory';

/**
 * Version History Service
 * Handles tracking and retrieving edit history for all CMS entities
 */
class VersionHistoryService {
  private storage: Map<string, VersionHistory[]>;

  constructor() {
    this.storage = new Map();
    this.loadFromLocalStorage();
  }

  /**
   * Track a change to an entity
   */
  track(params: {
    entityType: string;
    entityId: string | number;
    action: 'create' | 'update' | 'delete';
    changes: Record<string, { old: unknown; new: unknown }>;
    snapshot?: Record<string, unknown>;
    user?: string;
  }): void {
    const { entityType, entityId, action, changes, snapshot, user } = params;
    
    const key = `${entityType}:${entityId}`;
    const history: VersionHistory = {
      id: Date.now(),
      entityType,
      entityId,
      action,
      changes,
      changedBy: user || 'Unknown User',
      changedAt: new Date().toISOString(),
      snapshot,
    };

    const existing = this.storage.get(key) || [];
    existing.push(history);
    this.storage.set(key, existing);
    
    this.saveToLocalStorage();
  }

  /**
   * Get history for a specific entity
   */
  getHistory(entityType: string, entityId: string | number): VersionHistoryEntry[] {
    const key = `${entityType}:${entityId}`;
    const history = this.storage.get(key) || [];
    
    return history.map((h) => ({
      id: h.id!,
      action: h.action,
      user: h.changedBy,
      timestamp: h.changedAt,
      changes: this.formatChanges(h.changes),
      data: h.snapshot,
    })).reverse(); // Most recent first
  }

  /**
   * Get all history entries (for global history view)
   */
  getAllHistory(limit = 50): VersionHistoryEntry[] {
    const allHistory: VersionHistory[] = [];
    
    this.storage.forEach((histories) => {
      allHistory.push(...histories);
    });
    
    // Sort by date descending
    allHistory.sort((a, b) => 
      new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime()
    );
    
    return allHistory.slice(0, limit).map((h) => ({
      id: h.id!,
      action: h.action,
      user: h.changedBy,
      timestamp: h.changedAt,
      changes: this.formatChanges(h.changes),
      data: h.snapshot,
    }));
  }

  /**
   * Clear history for a specific entity
   */
  clearHistory(entityType: string, entityId: string | number): void {
    const key = `${entityType}:${entityId}`;
    this.storage.delete(key);
    this.saveToLocalStorage();
  }

  /**
   * Format changes into a human-readable summary
   */
  private formatChanges(changes: Record<string, { old: unknown; new: unknown }>): string {
    const fields = Object.keys(changes);
    
    if (fields.length === 0) return 'No changes';
    if (fields.length === 1) return `Updated ${fields[0]}`;
    if (fields.length <= 3) return `Updated ${fields.join(', ')}`;
    
    return `Updated ${fields.length} fields`;
  }

  /**
   * Save to localStorage for persistence
   */
  private saveToLocalStorage(): void {
    try {
      const data: Record<string, VersionHistory[]> = {};
      this.storage.forEach((value, key) => {
        data[key] = value;
      });
      localStorage.setItem('cms_version_history', JSON.stringify(data));
    } catch (error) {
    }
  }

  /**
   * Load from localStorage
   */
  private loadFromLocalStorage(): void {
    try {
      const data = localStorage.getItem('cms_version_history');
      if (data) {
        const parsed: Record<string, VersionHistory[]> = JSON.parse(data);
        Object.entries(parsed).forEach(([key, value]) => {
          this.storage.set(key, value);
        });
      }
    } catch (error) {
    }
  }
}

// Singleton instance
const versionHistoryService = new VersionHistoryService();

export default versionHistoryService;
